import { useLocation, useParams } from 'react-router'
import { ComponentProps, useEffect, useMemo, useState } from 'react'
import { Lock } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Tabs } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { useFinancialModelSettingsStore } from '@/feature/financial-model/settings/store/financialModelSettingsStore'
import { PageTabs } from '@/components/shared/page-sections.tsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'

export type TabValue = 'set-up' | 'historical-data' | 'forecast-data' | 'total'

const allowedTabs = [`historical-data`, `forecast-data`, `total`]

type ModuleNavigationProps = {
  isSetupCompleted: boolean
  handleTabsChange: (value: TabValue) => void
  children: React.ReactNode
  isTotalAllowed?: boolean | null
} & Omit<ComponentProps<typeof Tabs>, `defaultValue` | `onValueChange` | `children`>

export const ModuleNavigation = ({
  isSetupCompleted,
  handleTabsChange,
  children,
  className,
  isTotalAllowed = null,
  ...tabsProps
}: ModuleNavigationProps) => {
  const location = useLocation()
  const defaultTab = useMemo(() => location.pathname.split(`/`).pop() as TabValue, [location.pathname])
  const [tab, setTab] = useState<TabValue>(defaultTab)
  const { id: financialModelId } = useParams<{ id: string }>()
  const financialModelStore = useFinancialModelSettingsStore(settings => settings.settings[financialModelId!])
  const { t } = useTranslation(`common`)
  const hasForecastData = useMemo(
    () => financialModelStore.timeframe?.forecast.isActive ?? !!financialModelStore.timeframe?.forecast.startAt,
    [financialModelStore]
  )

  const hasHistoricalData = useMemo(
    () => financialModelStore.timeframe?.historical.isActive ?? !!financialModelStore.timeframe?.historical.startAt,
    [financialModelStore]
  )

  useEffect(() => {
    if (allowedTabs.includes(defaultTab)) {
      if (defaultTab !== tab) {
        setTab(defaultTab)
      }
    } else {
      setTab(`set-up`)
    }
  }, [defaultTab, tab])

  const tabsTriggers = useMemo(
    () => [
      {
        key: `set-up-tab`,
        value: `set-up`,
        defaultChecked: defaultTab !== `historical-data` && defaultTab !== `forecast-data`,
        children: t(`layout.tabs.setup`)
      },
      ...(hasHistoricalData
        ? [
            {
              key: `historical-data-tab`,
              value: `historical-data`,
              defaultChecked: defaultTab === `historical-data`,
              disabled: !isSetupCompleted,
              children: (
                <>
                  {t(`layout.tabs.historical_data`)}
                  {!isSetupCompleted && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Lock size={16} className='ml-2' />
                      </TooltipTrigger>
                      <TooltipContent>{t(`layout.tabs.tooltip_locked`)}</TooltipContent>
                    </Tooltip>
                  )}
                </>
              )
            }
          ]
        : []),
      ...(hasForecastData
        ? [
            {
              key: `forecast-data-tab`,
              value: `forecast-data`,
              defaultChecked: defaultTab === `forecast-data`,
              disabled: !isSetupCompleted,
              children: (
                <>
                  {t(`layout.tabs.forecast_data`)}
                  {!isSetupCompleted && <Lock size={16} className='ml-2' />}
                </>
              )
            }
          ]
        : []),
      {
        key: `total-tab`,
        value: `total`,
        defaultChecked: defaultTab === `total`,
        disabled: isTotalAllowed !== null ? !isTotalAllowed : !isSetupCompleted,
        children: (
          <>
            {t(`layout.tabs.total`)}
            {!isSetupCompleted && <Lock size={16} className='ml-2' />}
          </>
        )
      }
    ],
    [defaultTab, hasForecastData, hasHistoricalData, isSetupCompleted, isTotalAllowed, t]
  )

  return (
    <TooltipProvider>
      <PageTabs
        className={cn(className)}
        value={tab}
        onValueChange={value => {
          setTab(value as TabValue)
          handleTabsChange(value as TabValue)
        }}
        {...tabsProps}
        triggers={tabsTriggers}
      >
        {children}
      </PageTabs>
    </TooltipProvider>
  )
}
